<template>
  <div class="pagination">
    <div class="custom-pagination" :class="{ 'tight-spacing': totalPages <= 10 }">
      <button
        @click="handlePrevPage"
        class="control-prev"
        :disabled="page <= 1"
        aria-label="Previous page"
      >
        <Icon icon="mdi:chevron-left" width="16" height="16" style="margin-right: 6px" />
        Prev
      </button>
      <button
        v-for="pageNum in visiblePages"
        :key="pageNum"
        :class="[{ active: page === pageNum }, pageNum === '...' ? 'ellipsis' : '']"
        @click="changePage(pageNum)"
        :disabled="pageNum === '...'"
      >
        {{ pageNum }}
      </button>
      <button
        @click="handleNextPage"
        class="control-next"
        :disabled="page >= totalPages"
        aria-label="Next page"
      >
        Next
        <Icon icon="mdi:chevron-right" width="16" height="16" style="margin-left: 6px" />
      </button>
    </div>
    <div class="pagination-controls">
      <div class="per-page">
        <div class="per-page-select-wrapper">
          <Select
            :model-value="perPage.toString()"
            :options="perPageOptions"
            size="sm"
            @update:model-value="handlePerPageChange"
          />
        </div>
        <span class="per-page-label">per page</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { Icon } from "@iconify/vue";
import Select from "@/components/ui/Select.vue";
import type { PaginationState } from "./types";

interface Props {
  pagination: PaginationState;
  onPageChange?: (page: number) => void;
  onPerPageChange?: (perPage: number) => void;
  perPageOptions?: number[];
}

const props = withDefaults(defineProps<Props>(), {
  perPageOptions: () => [50, 100, 200],
});

const emit = defineEmits<{
  "update:currentPage": [page: number];
  "update:perPage": [perPage: number];
  "page-change": [page: number];
  "per-page-change": [perPage: number];
}>();

const page = computed(() => props.pagination.page);
const perPage = computed(() => props.pagination.perPage);
const total = computed(() => props.pagination.total);
const totalPages = computed(() => Math.ceil(total.value / perPage.value));

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = page.value;
  const pages: (number | string)[] = [];

  if (total <= 10) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    let startPage = Math.max(1, current - 5);
    let endPage = Math.min(total, current + 5);

    if (current <= 6) {
      startPage = 1;
      endPage = 10;
    } else if (current >= total - 5) {
      startPage = total - 9;
      endPage = total;
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < total) {
      pages.push("...");
    }
  }

  return pages;
});

const perPageOptions = computed(() =>
  props.perPageOptions.map((opt) => ({
    value: String(opt),
    label: String(opt),
  }))
);

const handlePrevPage = () => {
  if (page.value > 1) {
    const newPage = page.value - 1;
    emit("update:currentPage", newPage);
    emit("page-change", newPage);
    props.onPageChange?.(newPage);
  }
};

const handleNextPage = () => {
  if (page.value < totalPages.value) {
    const newPage = page.value + 1;
    emit("update:currentPage", newPage);
    emit("page-change", newPage);
    props.onPageChange?.(newPage);
  }
};

const changePage = (pageNum: number | string) => {
  if (pageNum === "..." || pageNum === page.value) return;
  const pageNumber = pageNum as number;
  emit("update:currentPage", pageNumber);
  emit("page-change", pageNumber);
  props.onPageChange?.(pageNumber);
};

const handlePerPageChange = (newPerPage: string | number) => {
  const perPageValue = Number(newPerPage);
  emit("update:perPage", perPageValue);
  emit("update:currentPage", 1);
  emit("per-page-change", perPageValue);
  props.onPerPageChange?.(perPageValue);
  props.onPageChange?.(1);
};
</script>

<style scoped>
.pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  column-gap: 24px;
  row-gap: 10px;
  padding: 8px 0;
}

.custom-pagination {
  display: flex;
  align-items: center;
  gap: 8px;
}

.custom-pagination.tight-spacing {
  gap: 4px;
}

.custom-pagination button {
  padding: 4px 8px;
  border: 1px solid #d0d5dd;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.custom-pagination button:hover:not(:disabled) {
  background-color: #f6f4ff;
  border-color: #7d7df7;
}

.custom-pagination button.active {
  background-color: #7d7df7;
  color: white;
  border-color: #7d7df7;
}

.custom-pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.custom-pagination .ellipsis {
  border: none;
  cursor: default;
  padding: 4px 5px;
}

.custom-pagination .ellipsis:hover {
  background: white;
  border: none;
}

.pagination-controls {
  display: flex;
  align-items: center;
}

.per-page {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.per-page-select-wrapper {
  width: 70px;
  flex-shrink: 0;
}

.per-page-select-wrapper :deep(.ui-select) {
  width: 100%;
}

.per-page-select-wrapper :deep(.ui-select__field) {
  min-width: 0;
  width: 100%;
}

.per-page-label {
  font-size: 12px;
  color: #425166;
  white-space: nowrap;
  flex-shrink: 0;
}
</style>
